import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { deleteJson, getJson, patchJson, postJson } from "@/lib/api"
import { captureEvent } from "@/lib/analytics"
import type {
  AlertConditionType,
  AlertDirection,
  AlertRepeatMode,
  ChartAlertable,
  ChartDrawing,
  ChartOverlay,
  ChartToolMode,
  PriceAlertRecord,
} from "@/types/chartOverlays"
import { alertToOverlay, drawingToOverlay } from "@/types/chartOverlays"

interface Suggestion {
  kind: ChartDrawing["kind"]
  price: number
  origin_time: string | null
  label: string
  source: ChartDrawing["source"]
}

export function useChartAnnotations(alertable: ChartAlertable | null, signedIn: boolean) {
  const [drawings, setDrawings] = useState<ChartDrawing[]>([])
  const [alerts, setAlerts] = useState<PriceAlertRecord[]>([])
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [dismissedSuggestionKeys, setDismissedSuggestionKeys] = useState<string[]>([])
  const [toolMode, setToolMode] = useState<ChartToolMode>("pan")
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestRef = useRef(0)
  const drawingsRef = useRef(drawings)
  drawingsRef.current = drawings

  const loadData = useCallback(async (): Promise<void> => {
    if (!signedIn || !alertable) {
      setDrawings([])
      setAlerts([])
      return
    }
    const requestId = ++requestRef.current
    try {
      const params = `alertable_type=${alertable.type}&alertable_id=${alertable.id}`
      const [drawingsResponse, alertsResponse] = await Promise.all([
        getJson<{ drawings: ChartDrawing[] }>(`/api/chart_drawings?${params}`),
        getJson<{ alerts: PriceAlertRecord[] }>(`/api/price_alerts?${params}`),
      ])
      if (requestId !== requestRef.current) return
      setDrawings(drawingsResponse.drawings)
      setAlerts(alertsResponse.alerts)
    } catch {
      if (requestId === requestRef.current) setError("Chart annotations could not be loaded")
    }
  }, [alertable, signedIn])

  const loadSuggestions = useCallback(async (): Promise<void> => {
    if (!signedIn || !alertable || !showSuggestions) return
    try {
      const params = `alertable_type=${alertable.type}&alertable_id=${alertable.id}`
      const response = await getJson<{ suggestions: Suggestion[] }>(`/api/chart_drawings/suggestions?${params}`)
      setSuggestions(response.suggestions)
    } catch {
      setError("Suggestions could not be loaded")
    }
  }, [alertable, showSuggestions, signedIn])

  useEffect(() => {
    void loadData()
  }, [loadData])

  useEffect(() => {
    void loadSuggestions()
  }, [loadSuggestions])

  const overlays = useMemo((): ChartOverlay[] => {
    const drawingOverlays = drawings.map((drawing) => drawingToOverlay(drawing))
    const alertOverlays = alerts
      .map((alert) => alertToOverlay(alert))
      .filter((overlay): overlay is ChartOverlay => overlay !== null)
    const suggestionOverlays = showSuggestions
      ? suggestions
          .filter((item) => !dismissedSuggestionKeys.includes(`${item.kind}-${item.price}`))
          .map((item, index) => ({
            id: `suggestion-${index}`,
            kind: item.kind,
            source: item.source,
            price: item.price,
            origin_time: item.origin_time,
            label: item.label,
            color: "#fbbf24",
            ephemeral: true,
          }))
      : []
    return [...drawingOverlays, ...alertOverlays, ...suggestionOverlays]
  }, [alerts, dismissedSuggestionKeys, drawings, showSuggestions, suggestions])

  const placeDrawing = useCallback(
    async (price: number, originTime: string | null): Promise<void> => {
      if (!alertable || toolMode === "pan") return
      try {
        const response = await postJson<{ drawing: ChartDrawing }>("/api/chart_drawings", {
          chart_drawing: {
            kind: toolMode,
            source: "user",
            price,
            origin_time: originTime,
          },
          alertable_type: alertable.type,
          alertable_id: alertable.id,
        })
        setDrawings((current) => [response.drawing, ...current])
        captureEvent("chart_drawing_created", { kind: toolMode, alertable_type: alertable.type })
        setToolMode("pan")
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : "Drawing could not be saved")
      }
    },
    [alertable, toolMode],
  )

  const moveDrawing = useCallback(async (overlayId: string, price: number): Promise<void> => {
    const drawingId = Number(overlayId)
    if (!Number.isFinite(drawingId)) return
    const previousPrice = drawingsRef.current.find((drawing) => drawing.id === drawingId)?.price ?? null
    setDrawings((current) =>
      current.map((drawing) => (drawing.id === drawingId ? { ...drawing, price } : drawing)),
    )
    try {
      const response = await patchJson<{ drawing: ChartDrawing }>(`/api/chart_drawings/${drawingId}`, {
        chart_drawing: { price },
      })
      setDrawings((current) =>
        current.map((drawing) => (drawing.id === drawingId ? response.drawing : drawing)),
      )
    } catch {
      if (previousPrice !== null) {
        setDrawings((current) =>
          current.map((drawing) => (drawing.id === drawingId ? { ...drawing, price: previousPrice } : drawing)),
        )
      }
      setError("Drawing could not be moved")
    }
  }, [])

  const removeDrawing = useCallback(async (drawingId: number): Promise<void> => {
    try {
      await deleteJson(`/api/chart_drawings/${drawingId}`)
      setDrawings((current) => current.filter((drawing) => drawing.id !== drawingId))
    } catch {
      setError("Drawing could not be removed")
    }
  }, [])

  // Only saved drawings have numeric ids; alerts and suggestions can't be deleted here.
  const selectedDrawingId =
    selectedOverlayId !== null && /^\d+$/.test(selectedOverlayId) ? Number(selectedOverlayId) : null

  const removeSelectedDrawing = useCallback(async (): Promise<void> => {
    if (selectedDrawingId === null) return
    await removeDrawing(selectedDrawingId)
    setSelectedOverlayId(null)
  }, [removeDrawing, selectedDrawingId])

  const acceptSuggestion = useCallback(
    async (suggestion: Suggestion): Promise<void> => {
      if (!alertable) return
      try {
        const response = await postJson<{ drawing: ChartDrawing }>("/api/chart_drawings", {
          chart_drawing: {
            kind: suggestion.kind,
            source: "ai_suggested",
            price: suggestion.price,
            origin_time: suggestion.origin_time,
            label: suggestion.label,
          },
          alertable_type: alertable.type,
          alertable_id: alertable.id,
        })
        setDrawings((current) => [response.drawing, ...current])
        setDismissedSuggestionKeys((current) => [...current, `${suggestion.kind}-${suggestion.price}`])
        captureEvent("chart_suggestion_accepted", { alertable_type: alertable.type })
      } catch {
        setError("Suggestion could not be saved")
      }
    },
    [alertable],
  )

  const dismissSuggestion = useCallback((suggestion: Suggestion): void => {
    setDismissedSuggestionKeys((current) => [...current, `${suggestion.kind}-${suggestion.price}`])
  }, [])

  const createAlert = useCallback(
    async (input: {
      condition_type: AlertConditionType
      direction: AlertDirection
      threshold: number
      repeat_mode?: AlertRepeatMode
      chart_drawing_id?: number
    }): Promise<void> => {
      if (!alertable) return
      setError(null)
      try {
        const response = await postJson<{ alert: PriceAlertRecord }>("/api/price_alerts", {
          price_alert: input,
          alertable_type: alertable.type,
          alertable_id: alertable.id,
        })
        setAlerts((current) => [response.alert, ...current])
        captureEvent("price_alert_created", {
          condition_type: input.condition_type,
          alertable_type: alertable.type,
        })
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : "Alert could not be created")
      }
    },
    [alertable],
  )

  const updateAlert = useCallback(async (alertId: number, patch: Partial<PriceAlertRecord>): Promise<void> => {
    try {
      const response = await patchJson<{ alert: PriceAlertRecord }>(`/api/price_alerts/${alertId}`, {
        price_alert: patch,
      })
      setAlerts((current) => current.map((alert) => (alert.id === alertId ? response.alert : alert)))
    } catch {
      setError("Alert could not be updated")
    }
  }, [])

  const removeAlert = useCallback(async (alertId: number): Promise<void> => {
    try {
      await deleteJson(`/api/price_alerts/${alertId}`)
      setAlerts((current) => current.filter((alert) => alert.id !== alertId))
    } catch {
      setError("Alert could not be removed")
    }
  }, [])

  return {
    drawings,
    alerts,
    suggestions,
    overlays,
    toolMode,
    setToolMode,
    showSuggestions,
    setShowSuggestions,
    error,
    setError,
    placeDrawing,
    moveDrawing,
    removeDrawing,
    selectedDrawingId,
    setSelectedOverlayId,
    removeSelectedDrawing,
    acceptSuggestion,
    dismissSuggestion,
    createAlert,
    updateAlert,
    removeAlert,
    reload: loadData,
  }
}
